// ./src/components/features/PrintableSubmission/PrintableFileList.tsx
import React from "react";
import { Box, Text, Stack, Group } from "@mantine/core";

import { SubmissionFile } from "@/utils/api";
import { AppIcon } from "@/components/generic";

export interface PrintableFileListProps {
  /** List of submission files */
  files: SubmissionFile[];
}

/**
 * Formats file size in human-readable format
 */
const formatFileSize = (bytes: number | string | undefined): string => {
  if (bytes === undefined || bytes === null) return "";

  const numBytes = typeof bytes === "string" ? parseInt(bytes, 10) : bytes;

  if (isNaN(numBytes) || numBytes === 0) return "0 B";

  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(numBytes) / Math.log(k));
  return `${parseFloat((numBytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
};

/**
 * Gets a simple file type label from MIME type
 */
const getFileTypeLabel = (mimeType: string | undefined): string => {
  if (!mimeType) return "File";

  const mimeMap: Record<string, string> = {
    // Documents
    "application/pdf": "PDF",
    "application/msword": "Word",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
      "Word",
    "application/vnd.ms-excel": "Excel",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
      "Excel",
    "application/vnd.ms-powerpoint": "PowerPoint",
    "application/vnd.openxmlformats-officedocument.presentationml.presentation":
      "PowerPoint",
    "text/plain": "Text",
    "text/csv": "CSV",
    "application/json": "JSON",
    "application/xml": "XML",
    "text/xml": "XML",

    // Images
    "image/jpeg": "Image",
    "image/png": "Image",
    "image/gif": "Image",
    "image/webp": "Image",
    "image/svg+xml": "SVG",
    "image/bmp": "Image",
    "image/tiff": "Image",

    // Audio
    "audio/mpeg": "Audio",
    "audio/wav": "Audio",
    "audio/ogg": "Audio",
    "audio/mp4": "Audio",

    // Video
    "video/mp4": "Video",
    "video/mpeg": "Video",
    "video/webm": "Video",
    "video/quicktime": "Video",

    // Archives
    "application/zip": "Archive",
    "application/x-rar-compressed": "Archive",
    "application/x-7z-compressed": "Archive",
    "application/gzip": "Archive",
  };

  return mimeMap[mimeType] || "File";
};

/**
 * Gets icon name based on MIME type
 */
const getFileIconName = (mimeType: string | undefined): string => {
  if (!mimeType) return "File";

  if (mimeType.startsWith("image/")) return "Image";
  if (mimeType.startsWith("video/")) return "Video";
  if (mimeType.startsWith("audio/")) return "Music";
  if (mimeType === "application/pdf") return "FileText";
  if (mimeType.includes("word") || mimeType.includes("document"))
    return "FileText";
  if (mimeType.includes("excel") || mimeType.includes("spreadsheet"))
    return "Table";
  if (mimeType.includes("powerpoint") || mimeType.includes("presentation"))
    return "Presentation";
  if (
    mimeType.includes("zip") ||
    mimeType.includes("rar") ||
    mimeType.includes("7z") ||
    mimeType.includes("gzip")
  )
    return "Archive";

  return "File";
};

export const PrintableFileList: React.FC<PrintableFileListProps> = ({
  files,
}) => {
  if (!files || files.length === 0) {
    return null;
  }

  return (
    <Box className="print-section" mt="md">
      <Text
        size="sm"
        fw={700}
        mb="sm"
        style={{
          textTransform: "uppercase",
          letterSpacing: "0.5px",
          borderBottom: "2px solid var(--mantine-color-gray-4)",
          paddingBottom: "8px",
        }}
      >
        Uploaded Files ({files.length})
      </Text>

      <Stack gap="xs">
        {files.map((file, index) => (
          <Box
            key={file.id || index}
            className="print-field-group"
            py="xs"
            style={{
              borderBottom: "1px solid var(--mantine-color-gray-2)",
            }}
          >
            <Group gap="sm" wrap="nowrap">
              {/* File icon */}
              <Box
                style={{
                  flexShrink: 0,
                  width: 32,
                  height: 32,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "var(--mantine-color-gray-1)",
                  borderRadius: "4px",
                }}
              >
                <AppIcon
                  iconName={getFileIconName(file.mimeType)}
                  size={18}
                  color="var(--mantine-color-gray-6)"
                />
              </Box>

              {/* File info */}
              <Box style={{ flex: 1, minWidth: 0 }}>
                <Text
                  size="sm"
                  fw={500}
                  style={{
                    wordBreak: "break-word",
                    lineHeight: 1.3,
                  }}
                >
                  {file.fileName || "Unnamed file"}
                </Text>

                <Group gap="xs" mt={2}>
                  <Text size="xs" c="dimmed">
                    {getFileTypeLabel(file.mimeType)}
                  </Text>
                  {file.fileSize && (
                    <>
                      <Text size="xs" c="dimmed">
                        •
                      </Text>
                      <Text size="xs" c="dimmed">
                        {formatFileSize(file.fileSize)}
                      </Text>
                    </>
                  )}
                </Group>
              </Box>
            </Group>
          </Box>
        ))}
      </Stack>
    </Box>
  );
};
